import { bbox, distance, point } from '@turf/turf'
import { xorBy } from 'es-toolkit'
import type { BBox } from 'geojson'
import type { AssetLocation } from '../../../data/locations.tsx'

export const toggleLocation = (selected: AssetLocation[], location: AssetLocation) => xorBy(selected, [location], ({ id }) => id)

const boundingBoxes = new WeakMap<AssetLocation['geometry'], BBox>()

const boundingBoxOf = ({ geometry }: AssetLocation) => {
	const cached = boundingBoxes.get(geometry)
	if (cached) return cached
	const computed = bbox(geometry)
	boundingBoxes.set(geometry, computed)
	return computed
}

// Where two ranges are nearest to each other; ranges that overlap meet anywhere inside the overlap
const nearestOnAxis = (aMin: number, aMax: number, bMin: number, bMax: number): [number, number] => {
	if (aMax < bMin) return [aMax, bMin]
	if (bMax < aMin) return [aMin, bMax]
	const shared = Math.max(aMin, bMin)
	return [shared, shared]
}

// Meridians converge toward the poles, so ranges that overlap are nearest at the shared latitude farthest from the equator
const nearestLatitudes = (aSouth: number, aNorth: number, bSouth: number, bNorth: number): [number, number] => {
	const [southEdge, northEdge] = [Math.max(aSouth, bSouth), Math.min(aNorth, bNorth)]
	if (southEdge > northEdge) return nearestOnAxis(aSouth, aNorth, bSouth, bNorth)
	const shared = Math.abs(northEdge) > Math.abs(southEdge) ? northEdge : southEdge
	return [shared, shared]
}

// The gap between the bounding boxes, so that a long line counts by its ends and not by its center
const distanceBetweenKm = (a: AssetLocation, b: AssetLocation) => {
	const [aWest, aSouth, aEast, aNorth] = boundingBoxOf(a)
	const [bWest, bSouth, bEast, bNorth] = boundingBoxOf(b)
	const [aLon, bLon] = nearestOnAxis(aWest ?? 0, aEast ?? 0, bWest ?? 0, bEast ?? 0)
	const [aLat, bLat] = nearestLatitudes(aSouth ?? 0, aNorth ?? 0, bSouth ?? 0, bNorth ?? 0)
	return distance(point([aLon, aLat]), point([bLon, bLat]), { units: 'kilometers' })
}

// With no limit, any assets are close enough
export const areWithinSelectionDistance = (locations: AssetLocation[], maxDistanceKm: number | undefined) =>
	maxDistanceKm === undefined ||
	locations.every((a, index) => locations.slice(index + 1).every((b) => distanceBetweenKm(a, b) <= maxDistanceKm))
